import styled from '@emotion/styled';
import { BillingAddress, GetBillingDetailsResponse, TaxID } from '@journeyapps-platform/types-charging-station';
import { AddressElement, Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { loadStripe, StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { ENV } from '../../../../../env';
import { useSystem } from '../../../../../hooks/useSystem';
import { LoaderSpinner } from '../../../../../widgets/LoaderWidget';
import { ButtonType } from '../../../../../widgets/buttons/ButtonWidget';
import { GroupedButtons } from '../../../../../widgets/buttons/GroupedButtons';
import { FormLayoutWidget } from '../../../../../widgets/layout/FormLayoutWidget';
import { Formik } from 'formik';
import { FormikInputWidget } from '../../../../../widgets/inputs/FormikInput';
import { TaxIDWidget } from './TaxIDWidget';
import * as microErrors from '@journeyapps-platform/micro-errors';

export interface BillingDetailsFormProps {
  onBack: () => any;
  onNext: () => any;
  backButton?: string;
}

export const BillingDetailsForm: React.FC<BillingDetailsFormProps> = (props) => {
  const prom = loadStripe(ENV.STRIPE_PK);
  const [loading, setLoading] = useState(true);
  const system = useSystem();
  const org = system.orgStore.current_org;
  const [details, setDetails] = useState<GetBillingDetailsResponse>();
  const [taxID, setTaxID] = useState<TaxID | null>(null);
  const [addressComplete, setAddressComplete] = useState(false);
  useEffect(() => {
    if (org) {
      setLoading(true);
      org.loadBillingDetails().then(() => {
        if (org.billingDetails) {
          setDetails({
            address: org.billingDetails?.address,
            name: org.billingDetails?.name || ''
          });
          setTaxID(org.billingDetails.tax || null);
        }
        setLoading(false);
      });
    }
  }, [org]);

  const normalise = (address?: BillingAddress | StripeAddressElementChangeEvent['value']['address']) => {
    return {
      line1: address?.line1 || '',
      line2: address?.line2 || '',
      city: address?.city || '',
      state: address?.state || '',
      postal_code: address?.postal_code || '',
      country: address?.country || ''
    };
  };

  if (!org || loading) {
    return (
      <S.Container>
        <LoaderSpinner />
      </S.Container>
    );
  }

  return (
    <Elements
      stripe={prom}
      options={{
        mode: 'setup',
        currency: 'usd',
        fonts: [
          {
            // tried getting this from our own domain, but there are too many issues with CORS
            cssSrc: 'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap'
          }
        ],
        appearance: {
          theme: 'flat',
          rules: {
            '.Input': {
              lineHeight: 'normal',
              padding: '12px 18px',
              fontSize: '14px',
              fontFamily: system.theme.text.fontFamily,
              border: 'solid 1px transparent'
            },
            '.Label': {
              lineHeight: 'normal',
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: system.theme.text.fontFamily,
              marginBottom: '8px'
            }
          },
          variables: {
            borderRadius: '4px',
            colorBackground: system.theme.forms.input.background,
            colorText: system.theme.forms.input.color,
            focusOutline: 'none',
            focusBoxShadow: 'none'
          }
        }
      }}
    >
      <ElementsConsumer
        children={() => {
          return (
            <Formik
              initialValues={{ email: org.billingDetails?.email || '' }}
              onSubmit={async ({ email }) => {
                try {
                  setLoading(true);
                  await org.updateBillingDetails({
                    address: normalise(details?.address),
                    name: details?.name || '',
                    email: email,
                    tax: taxID || undefined
                  });
                  props.onNext();
                } catch (ex) {
                  if (microErrors.isJourneyError(ex)) {
                    await system.dialogStore.errorDialog({
                      title: 'Billing details error',
                      errorMessage: ex.errorData?.details || 'Please contact support'
                    });
                  } else {
                    await system.dialogStore.errorDialog({
                      title: 'Oops! Something went wrong',
                      errorMessage: 'Please contact support'
                    });
                  }
                } finally {
                  setLoading(false);
                }
              }}
            >
              {({ submitForm, values }) => {
                const enabled = details && addressComplete && values.email !== '' && details.name !== '';

                return (
                  <FormLayoutWidget
                    sections={[
                      {
                        key: 'address',
                        elements: [
                          <S.FormContainer>
                            <S.Column>
                              <AddressElement
                                onChange={({ value, complete }) => {
                                  setDetails({
                                    name: value.name,
                                    address: normalise(value.address)
                                  });
                                  setAddressComplete(complete);
                                }}
                                options={{
                                  mode: 'billing',
                                  display: {
                                    name: 'organization'
                                  },
                                  defaultValues: {
                                    name: details?.name!,
                                    address: normalise(details?.address)
                                  }
                                }}
                              />
                            </S.Column>
                            <S.Column>
                              <FormikInputWidget name="email" label="Billing email" />
                              <S.TaxId
                                initial_tax={taxID}
                                country_code={details?.address?.country}
                                onChange={setTaxID}
                              />
                            </S.Column>
                          </S.FormContainer>
                        ]
                      },
                      {
                        key: 'buttons',
                        elements: [
                          <GroupedButtons
                            btns={[
                              {
                                type: ButtonType.SECONDARY,
                                label: props.backButton || 'Back',
                                action: () => {
                                  props.onBack();
                                }
                              },
                              {
                                type: ButtonType.PRIMARY,
                                label: 'Save and continue',
                                disabled: !enabled,
                                action: async (event, loading) => {
                                  return submitForm();
                                }
                              }
                            ]}
                          />
                        ]
                      }
                    ]}
                  />
                );
              }}
            </Formik>
          );
        }}
      />
    </Elements>
  );
};

namespace S {
  export const Container = styled.div`
    padding: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  export const FormContainer = styled.div`
    display: flex;
    column-gap: 20px;
  `;

  export const TaxId = styled(TaxIDWidget)`
    margin-top: 12px;
  `;

  export const Column = styled.div`
    min-width: 300px;
  `;
}
